/**
 * Edit Conversation Modal Component
 * Modal for editing conversation name and icon
 */

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Conversation } from '@/types/conversations';
import { useSession } from '@/features/auth/hooks/useSession';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { EMOJI_DATA } from '@/common/utils/emoji-data';
import './NewConversationModal.css';

interface EditConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversation: Conversation | null;
}

export function EditConversationModal({ isOpen, onClose, conversation }: EditConversationModalProps) {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const queryClient = useQueryClient();
  const activeConversation = conversation;
  const [conversationName, setConversationName] = useState('');
  const [conversationIcon, setConversationIcon] = useState('📁');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize form when the selected conversation changes.
  useEffect(() => {
    if (activeConversation) {
      setConversationName(activeConversation.name);
      setConversationIcon(activeConversation.icon || '📁');
      setEmojiSearch('');
    }
  }, [activeConversation]);

  // Close on escape key
  useEscapeKey(onClose, isOpen);

  // Handle overlay clicks with buffer zone
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);

  // Filter emojis based on search
  const filteredEmojis = useMemo(() => {
    if (!emojiSearch.trim()) {
      return EMOJI_DATA;
    }
    const searchLower = emojiSearch.toLowerCase().trim();
    return EMOJI_DATA.filter(item =>
      item.keywords.some(keyword => keyword.includes(searchLower)) ||
      item.emoji === searchLower
    );
  }, [emojiSearch]);

  if (!isOpen || !activeConversation) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationName.trim() || !token) return;

    setIsSaving(true);
    try {
      // Canonical conversation title update.
      await api.workspaces.updateConversation(
        token,
        workspaceId,
        activeConversation.id,
        { title: conversationName.trim() },
      );

      // Persist icon on canonical conversation record.
      if (conversationIcon !== (activeConversation.icon || '📁')) {
        await api.workspaces.updateConversation(
          token,
          workspaceId,
          activeConversation.id,
          { icon: conversationIcon },
        );
      }

      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(activeConversation.id) });

      onClose();
    } catch (err) {
      console.error('[EditConversationModal] Failed to update conversation:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <div className="modal-overlay" onMouseDown={handleOverlayMouseDown}
 onClick={handleOverlayClick}>
      <div className="modal-content new-conversation-modal xv-scroll" ref={modalContentRef} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Conversation</h2>
          <button className="modal-close-btn" onClick={onClose} type="button">×</button>
        </div>

        <form onSubmit={handleSubmit} className="new-conversation-form">
          {/* Conversation Name */}
          <div className="form-section">
            <input
              type="text"
              className="form-input"
              placeholder="Conversation name..."
              value={conversationName}
              onChange={(e) => setConversationName(e.target.value)}
              autoFocus
              required
            />
          </div>

          {/* Conversation Icon */}
          <div className="form-section">
            <input
              type="text"
              className="form-input emoji-search-input"
              placeholder="Search emojis... (e.g., work, travel, music)"
              value={emojiSearch}
              onChange={(e) => setEmojiSearch(e.target.value)}
            />
            <div className="icon-picker xv-scroll">
              {filteredEmojis.length === 0 ? (
                <div className="no-emojis-found">No emojis found for "{emojiSearch}"</div>
              ) : (
                filteredEmojis.map((item) => (
                  <button
                    key={item.emoji}
                    type="button"
                    className={`icon-option ${conversationIcon === item.emoji ? 'selected' : ''}`}
                    onClick={() => setConversationIcon(item.emoji)}
                    title={item.keywords.join(', ')}
                  >
                    {item.emoji === ':xavior-logo:' ? (
                      <img src="/favicon.svg" alt="Xavior" className="icon-logo" />
                    ) : (
                      item.emoji
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!conversationName.trim() || isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render to portal
  const portalRoot = document.getElementById('modal-portal-root');
  if (portalRoot) {
    return createPortal(modalContent, portalRoot);
  }
  return createPortal(modalContent, document.body);
}
